import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import useAsyncErrorHandler from "../../../../helpers/useAsyncErrorHandler"
import "../../config";

/**
 * @param {import("../../index").ArcGISMapProps} props
 */
function ArcGISMap(props) {
  const {
    onMapInitialized,
    mapProperties,
    viewProperties,
    view: viewType,
    containerProperties,
  } = props;

  const [currentMap, setCurrentMap] = useState();
  const [currentView, setCurrentView] = useState();

  /** @type {React.MutableRefObject<HTMLDivElement>} */
  const mapContainerRef = useRef();

  const errorHandler = useAsyncErrorHandler();

  useEffect(() => {
    let createdMap;
    async function createMap() {
      const Map = (await import("@arcgis/core/Map")).default;
      createdMap = new Map({
        ...mapProperties,
      })
      setCurrentMap(createdMap);
    }

    // Unawaited promises must have their errors caught
    createMap().catch(errorHandler);

    return () => {
      createdMap?.destroy();
      setCurrentMap(null);
    };
  }, [errorHandler]);

  useEffect(() => {
    let createdView;
    if(!mapContainerRef.current){
      return;
    }
    if (!currentMap) {
      // Must wait until the Map ctor has been imported & called
      return;
    }

    async function createView() {
      const ViewClass =
        viewType === "2d"
          ? (await import("@arcgis/core/views/MapView")).default
          : viewType === "3d"
          ? (await import("@arcgis/core/views/SceneView")).default
          : undefined;

      if (!ViewClass) {
        throw new Error(
          `View class constructor was not returned for view type ${viewType}`
        );
      }    

      createdView = new ViewClass({
        container: mapContainerRef.current,
        map: currentMap,
        ...viewProperties,
      })
      setCurrentView(createdView);
    }

    // Unawaited promises must have their errors caught
    createView().catch(errorHandler);

    return () => {
      createdView?.destroy();
      setCurrentView(null);
    };
  }, [mapContainerRef, currentMap, viewType, errorHandler]);

  useEffect(() => {
    if (!currentMap || !currentView || currentMap?.destroyed || currentView?.destroyed) {
      return;
    }
    onMapInitialized?.(currentMap, currentView);
  }, [onMapInitialized, currentMap, currentView]);

  return <div {...containerProperties} ref={mapContainerRef}/>;
}

ArcGISMap.propTypes = {
  view: PropTypes.oneOf(["2d", "3d"]),
  viewProperties: PropTypes.object.isRequired,
  mapProperties: PropTypes.object.isRequired,
  onMapInitialized: PropTypes.func,
  containerProperties: PropTypes.object,
};
export default ArcGISMap;
